import {
  BROWSER_AUTOMATION_TOOL_NAME,
  BrowserAutomationInputSchema,
  BrowserAutomationStorageSchema,
  formatBrowserAutomationActionLabel,
  INTERCEPT_TOGGLE_TOOL_NAME,
  INTERCEPT_RULE_TOOL_NAME,
  LAUNCH_BROWSER_TOOL_NAME,
  MATCH_REPLACE_RULE_TOOL_NAME,
  PORTAL_TOOL_NAVIGATE_EVENT,
  PortalToolNavigationSchema,
  PROXY_CONTROL_TOOL_NAME,
  PROXY_HISTORY_TOOL_NAME,
  REPLAY_REQUEST_TOOL_NAME,
  TRACK_HISTORY_REQUEST_TOOL_NAME,
  TrackHistoryRequestInputSchema,
  TrackHistoryRequestOutputSchema,
  InterceptToggleInputSchema,
  InterceptToggleOutputSchema,
  InterceptRuleOutputSchema,
  LaunchBrowserInputSchema,
  MatchReplaceRuleOutputSchema,
  MatchReplaceRuleToolInputSchema,
  ProxyHistoryInputSchema,
  ProxyHistoryOutputSchema,
  ReplayFromHistoryToolInputSchema,
  isPlainRecord,
  type PortalToolNavigation,
} from '@terra/shared-dto-chat-types';
import {
  ChromeIcon,
  Database01Icon,
  WorkHistoryIcon,
  PlayIcon,
  RefreshIcon,
  Shield01Icon,
  SlidersHorizontalIcon,
} from '@terra/ui-icons';
import type { IconComponent } from '../../tool-config-types';

export type PortalToolDisplayConfig = {
  label: string;
  icon: IconComponent;
  runningText: string;
  formatActionLabel: (input: unknown) => string;
  formatResultSummary: (output: unknown) => string | undefined;
  formatCardDetail?: (params: {
    input: unknown;
    output: unknown;
  }) => string | undefined;
  getNavigation: (output: unknown) => PortalToolNavigation | undefined;
};

function readPortalOutput(output: unknown): {
  message?: string;
  navigation?: PortalToolNavigation;
  data?: Record<string, unknown>;
} {
  if (!isPlainRecord(output)) {
    return {};
  }

  const navigation = PortalToolNavigationSchema.safeParse(output.navigation);

  return {
    message: typeof output.message === 'string' ? output.message : undefined,
    navigation: navigation.success ? navigation.data : undefined,
    data: isPlainRecord(output.data) ? output.data : undefined,
  };
}

function readAction(input: unknown): string | undefined {
  if (!isPlainRecord(input)) {
    return undefined;
  }

  return typeof input.action === 'string' ? input.action : undefined;
}

export const PORTAL_TOOL_DISPLAY_CONFIG: Record<
  string,
  PortalToolDisplayConfig
> = {
  [PROXY_CONTROL_TOOL_NAME]: {
    label: 'Proxy',
    icon: SlidersHorizontalIcon,
    runningText: 'Running proxy control...',
    formatActionLabel: (input) => {
      const action = readAction(input);

      return action ? `Proxy: ${action}` : 'Proxy control';
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ output }) => {
      const { data } = readPortalOutput(output);

      if (!data) {
        return undefined;
      }

      const parts: string[] = [];

      if (typeof data.isRunning === 'boolean') {
        parts.push(data.isRunning ? 'Running' : 'Stopped');
      }

      if (typeof data.port === 'number') {
        parts.push(`HTTP :${data.port}`);
      }

      if (typeof data.httpsPort === 'number') {
        parts.push(`HTTPS :${data.httpsPort}`);
      }

      if (typeof data.interceptEnabled === 'boolean') {
        parts.push(`Intercept: ${data.interceptEnabled ? 'on' : 'off'}`);
      }

      if (typeof data.matchReplaceEnabled === 'boolean') {
        parts.push(`Match/replace: ${data.matchReplaceEnabled ? 'on' : 'off'}`);
      }

      return parts.length > 0 ? parts.join(' · ') : undefined;
    },
    getNavigation: () => undefined,
  },
  [INTERCEPT_TOGGLE_TOOL_NAME]: {
    label: 'Intercept',
    icon: Shield01Icon,
    runningText: 'Updating intercept...',
    formatActionLabel: (input) => {
      const action = readAction(input);

      return action ? `Intercept: ${action}` : 'Intercept';
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ input, output }) => {
      const parsed = InterceptToggleInputSchema.safeParse(input);

      if (!parsed.success) {
        return undefined;
      }

      const ACTION_LABELS: Record<string, string> = {
        'enable-requests': 'Intercepting requests',
        'enable-responses': 'Intercepting responses',
        'enable-all': 'Intercepting requests & responses',
        disable: 'Intercept off',
        forward: 'Forwarding queued item',
        'forward-all': 'Forwarding all queued items',
        drop: 'Dropping queued item',
        'drop-all': 'Dropping all queued items',
        'list-queue': 'Listing intercept queue',
      };
      const label = ACTION_LABELS[parsed.data.action];
      const { data } = readPortalOutput(output);
      const parsedOutput = InterceptToggleOutputSchema.safeParse(output);
      const queue = parsedOutput.success
        ? parsedOutput.data.data?.queue
        : undefined;

      if (queue && queue.length > 0) {
        return queue
          .map(
            (item) =>
              `${item.queueItemId} · ${item.method} ${item.url} · ${item.status}`,
          )
          .join('\n');
      }

      if (
        data &&
        typeof data.queueLength === 'number' &&
        (parsed.data.action === 'forward' ||
          parsed.data.action === 'drop' ||
          parsed.data.action === 'forward-all' ||
          parsed.data.action === 'drop-all')
      ) {
        const processed =
          typeof data.processedCount === 'number'
            ? ` · ${data.processedCount} item${data.processedCount !== 1 ? 's' : ''} processed`
            : '';

        return label ? `${label}${processed}` : undefined;
      }

      if (
        data &&
        typeof data.queueLength === 'number' &&
        data.queueLength > 0
      ) {
        return label
          ? `${label} · ${data.queueLength} item${data.queueLength !== 1 ? 's' : ''} in queue`
          : undefined;
      }

      return label;
    },
    getNavigation: (output) => readPortalOutput(output).navigation,
  },
  [INTERCEPT_RULE_TOOL_NAME]: {
    label: 'Intercept Rules',
    icon: Shield01Icon,
    runningText: 'Updating intercept scope rules...',
    formatActionLabel: (input) => {
      const action = readAction(input);

      return action ? `Intercept rules: ${action}` : 'Intercept rules';
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ output }) => {
      const parsedOutput = InterceptRuleOutputSchema.safeParse(output);
      const rules = parsedOutput.success
        ? parsedOutput.data.data?.rules
        : undefined;

      if (rules && rules.length > 0) {
        return rules
          .map(
            (rule) => `${rule.ruleName} · ${rule.ruleId} · ${rule.urlPattern}`,
          )
          .join('\n');
      }

      return undefined;
    },
    getNavigation: (output) => readPortalOutput(output).navigation,
  },
  [MATCH_REPLACE_RULE_TOOL_NAME]: {
    label: 'Match & Replace',
    icon: RefreshIcon,
    runningText: 'Updating match/replace rules...',
    formatActionLabel: (input) => {
      const action = readAction(input);

      return action ? `Match/Replace: ${action}` : 'Match & Replace';
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ input, output }) => {
      const parsedOutput = MatchReplaceRuleOutputSchema.safeParse(output);
      const rules = parsedOutput.success
        ? parsedOutput.data.data?.rules
        : undefined;

      if (rules && rules.length > 0) {
        return rules
          .map(
            (rule) =>
              `${rule.ruleName} · ${rule.ruleId} · ${rule.matchType} in ${rule.matchLocation}`,
          )
          .join('\n');
      }

      const parsed = MatchReplaceRuleToolInputSchema.safeParse(input);
      const { data } = readPortalOutput(output);

      if (data?.ruleName) {
        return `Rule: ${data.ruleName}${data.ruleId ? ` · ${data.ruleId}` : ''}`;
      }

      if (!parsed.success || !parsed.data.rule) {
        return undefined;
      }

      const { rule, action } = parsed.data;

      if (
        action === 'list' ||
        action === 'remove' ||
        action === 'toggle-engine'
      ) {
        return undefined;
      }

      const lines: string[] = [];

      lines.push(`${rule.matchType} in ${rule.matchLocation}`);
      lines.push(`Pattern: ${rule.matchPattern}`);
      if (rule.headerName) {
        lines.push(`Header: ${rule.headerName}`);
      }

      if (rule.replaceWith) {
        lines.push(`Replace with: ${rule.replaceWith}`);
      }

      if (rule.isRegex) {
        lines.push('Regex: yes');
      }

      if (rule.enabled === false) {
        lines.push('Enabled: no');
      }

      return lines.join(' · ');
    },
    getNavigation: (output) => readPortalOutput(output).navigation,
  },
  [REPLAY_REQUEST_TOOL_NAME]: {
    label: 'Replay',
    icon: PlayIcon,
    runningText: 'Sending replay request...',
    formatActionLabel: (input) => {
      const parsed = ReplayFromHistoryToolInputSchema.safeParse(input);

      if (!parsed.success) {
        return 'Replay request';
      }

      return `Replay history: ${parsed.data.requestId}`;
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ output }) => {
      const { data } = readPortalOutput(output);

      if (!data) {
        return undefined;
      }

      const parts: string[] = [];

      if (typeof data.statusCode === 'number' && data.statusCode > 0) {
        const msg =
          typeof data.statusMessage === 'string' && data.statusMessage
            ? ` ${data.statusMessage}`
            : '';

        parts.push(`${data.statusCode}${msg}`);
      }

      if (typeof data.durationMs === 'number') {
        parts.push(`${data.durationMs}ms`);
      }

      return parts.length > 0 ? parts.join(' · ') : undefined;
    },
    getNavigation: (output) => readPortalOutput(output).navigation,
  },
  [LAUNCH_BROWSER_TOOL_NAME]: {
    label: 'Browser',
    icon: ChromeIcon,
    runningText: 'Launching browser...',
    formatActionLabel: (input) => {
      const parsed = LaunchBrowserInputSchema.safeParse(input);

      if (!parsed.success) {
        return 'Browser: launch';
      }

      return `Browser: ${parsed.data.action}`;
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ input, output }) => {
      const inputParsed = LaunchBrowserInputSchema.safeParse(input);
      const parts: string[] = [];

      if (inputParsed.success && inputParsed.data.startUrl) {
        parts.push(inputParsed.data.startUrl);
      }

      if (inputParsed.success && inputParsed.data.freshSession) {
        parts.push('Fresh session');
      }

      if (inputParsed.success && inputParsed.data.credentialId) {
        parts.push('With credential');
      }

      if (inputParsed.success && inputParsed.data.instanceId) {
        parts.push('Reuse session');
      }

      const { data } = readPortalOutput(output);

      if (data && data.focusedExisting === true) {
        parts.push('Focused existing window');
      }

      if (data && data.credentialInjected === true) {
        parts.push('Credentials injected');
      }

      if (data && typeof data.instanceId === 'string') {
        parts.push(`instance: ${data.instanceId}`);
      }

      if (data && typeof data.pageId === 'string') {
        parts.push(`page: ${data.pageId}`);
      }

      return parts.length > 0 ? parts.join(' · ') : undefined;
    },
    getNavigation: () => undefined,
  },
  [BROWSER_AUTOMATION_TOOL_NAME]: {
    label: 'Browser',
    icon: ChromeIcon,
    runningText: 'Running browser step...',
    formatActionLabel: formatBrowserAutomationActionLabel,
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ input, output }) => {
      const parsed = BrowserAutomationInputSchema.safeParse(input);
      const { data } = readPortalOutput(output);
      const parts: string[] = [];

      if (parsed.success && parsed.data.url) {
        parts.push(parsed.data.url);
      }

      if (data && typeof data.title === 'string' && data.title) {
        parts.push(data.title);
      }

      if (data && Array.isArray(data.elements)) {
        parts.push(`${data.elements.length} interactive elements`);
      }

      if (parsed.success && parsed.data.pageId) {
        parts.push(`page: ${parsed.data.pageId}`);
      }

      if (data?.completed === true) {
        parts.push('Completed');
      }

      const alertCount =
        (Array.isArray(data?.newAlerts) ? data.newAlerts.length : 0) ||
        (Array.isArray(data?.alerts) ? data.alerts.length : 0);

      if (alertCount > 0) {
        parts.push(`${alertCount} alert${alertCount !== 1 ? 's' : ''}`);
      }

      if (data?.storage) {
        const storageParsed = BrowserAutomationStorageSchema.safeParse(
          data.storage,
        );

        if (storageParsed.success) {
          const storageCount =
            storageParsed.data.localStorage.length +
            storageParsed.data.sessionStorage.length +
            storageParsed.data.cookies.length;

          parts.push(`${storageCount} storage entries`);
        }
      }

      if (data?.evaluateStatus === 'timeout') {
        parts.push('JS timeout');
      } else if (data?.evaluateStatus === 'error') {
        parts.push('JS error');
      } else if (data?.evaluateResult !== undefined) {
        parts.push('JS result');
      }

      return parts.length > 0 ? parts.join(' · ') : undefined;
    },
    getNavigation: () => undefined,
  },
  [PROXY_HISTORY_TOOL_NAME]: {
    label: 'Captured proxy requests',
    icon: WorkHistoryIcon,
    runningText: 'Reading captured proxy requests...',
    formatActionLabel: (input) => {
      const parsed = ProxyHistoryInputSchema.safeParse(input);

      if (!parsed.success) {
        return 'Proxy history';
      }

      return parsed.data.action === 'get'
        ? `History: get ${parsed.data.requestId ?? 'request'}`
        : `History: list (${parsed.data.limit})`;
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ output }) => {
      const parsed = ProxyHistoryOutputSchema.safeParse(output);

      if (!parsed.success || !parsed.data.data) {
        return undefined;
      }

      const { rows, total } = parsed.data.data;

      if (rows.length === 0) {
        return 'No rows';
      }

      const first = rows[0];
      const statusCode =
        first.statusCode !== null ? `${first.statusCode}` : 'pending';
      const totalSuffix = typeof total === 'number' ? ` · ${total} total` : '';

      return rows.length === 1
        ? `${first.method} ${first.url} (${statusCode})${totalSuffix}`
        : `${rows.length} requests${totalSuffix}`;
    },
    getNavigation: () => undefined,
  },
  [TRACK_HISTORY_REQUEST_TOOL_NAME]: {
    label: 'Track Request',
    icon: Database01Icon,
    runningText: 'Tracking request from proxy history...',
    formatActionLabel: (input) => {
      const parsed = TrackHistoryRequestInputSchema.safeParse(input);

      if (!parsed.success) {
        return 'Track request';
      }

      return `Track: ${parsed.data.requestId}`;
    },
    formatResultSummary: (output) => readPortalOutput(output).message,
    formatCardDetail: ({ output }) => {
      const parsed = TrackHistoryRequestOutputSchema.safeParse(output);

      if (!parsed.success || !parsed.data.data) {
        return undefined;
      }

      const { requestId, tracked, skipped } = parsed.data.data;

      if (skipped) {
        return `${requestId} (skipped)`;
      }

      if (tracked) {
        return `${requestId} (tracked)`;
      }

      return requestId;
    },
    getNavigation: () => undefined,
  },
};
export function getPortalToolDisplayConfig(
  toolName?: string,
): PortalToolDisplayConfig | undefined {
  if (!toolName) {
    return undefined;
  }

  return PORTAL_TOOL_DISPLAY_CONFIG[toolName];
}

export function isPortalToolName(toolName?: string): boolean {
  return Boolean(toolName && PORTAL_TOOL_DISPLAY_CONFIG[toolName]);
}

export { PORTAL_TOOL_NAVIGATE_EVENT };
export function dispatchPortalToolNavigate(
  navigation: PortalToolNavigation,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(PORTAL_TOOL_NAVIGATE_EVENT, { detail: navigation }),
  );
}
